import { useState } from 'react';
import { motion } from 'framer-motion';
import { ScrambleText } from '@/components/ScrambleText';
import { Instagram } from 'lucide-react';

const CODE_LINE_4 = 'team.init([founder,curator,host]);project.build();gallery.launch(2026);';

const SCRAMBLE_CHARS = '01!@#$%{}[]<>/\\;:=+-~|^';

const team = [
  {
    name: 'MR. RELATIVE',
    role: 'FOUNDER OF THE RELATIVE GALLERY',
    detail: 'AI ARTIST, 500K+ FOLLOWERS',
    image: '/images/mr-relative.png',
    instagram: 'https://www.instagram.com/mr_relative_/',
    handle: 'mr_relative_',
  },
  {
    name: 'ODESSO',
    role: 'HEAD CURATOR',
    detail: 'MULTIDISCIPLINARY ARTIST, BFA',
    image: '/images/odesso.jpeg',
    instagram: 'https://www.instagram.com/odessoart/',
    handle: 'odessoart',
  },
  {
    name: 'MALGORZATA GAJEWSKA',
    role: 'FOUNDER OF LENIWKA STARY MLYN',
    detail: 'HOST, MANAGER, CHEF',
    image: '/images/malgorzata.jpeg',
    instagram: 'https://www.instagram.com/leniwka_/',
    handle: 'leniwka_',
  },
];

function InstagramLink({ href, handle }) {
  const [hovered, setHovered] = useState(false);
  const [display, setDisplay] = useState('// CONNECT');

  const onEnter = () => {
    setHovered(true);
    let frame = 0;
    const target = `@${handle}`;
    const interval = setInterval(() => {
      frame++;
      const progress = frame / 10;
      const result = target
        .split('')
        .map((c, i) => {
          if (c === ' ' || c === '@') return c;
          if (i / target.length < progress) return c;
          return SCRAMBLE_CHARS[Math.floor(Math.random() * SCRAMBLE_CHARS.length)];
        })
        .join('');
      setDisplay(result);
      if (frame >= 10) clearInterval(interval);
    }, 30);
  };

  const onLeave = () => {
    setHovered(false);
    setDisplay('// CONNECT');
  };

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`instagram-link-${handle}`}
      className="relative flex items-center gap-2.5 mt-3 py-2.5 px-3 -mx-3 border border-transparent hover:border-[#222222] hover:bg-[#0a0a0a] transition-all duration-300 z-10"
      style={{ cursor: 'pointer' }}
      onClick={(e) => { e.stopPropagation(); window.open(href, '_blank'); }}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
    >
      <span
        className={`transition-all duration-300 ${
          hovered ? 'text-white scale-110' : 'text-[#f6f6f6]'
        }`}
      >
        <Instagram className="w-4 h-4" />
      </span>
      <span
        className={`text-[11px] font-mono tracking-[0.1em] transition-colors duration-300 ${
          hovered ? 'text-[#cccccc]' : 'text-[#f6f6f6]'
        }`}
      >
        {display}
      </span>
    </a>
  );
}

export default function TeamSection() {
  return (
    <section
      data-testid="team-section"
      className="relative z-10 px-6 sm:px-10 py-20 lg:py-28"
    >
      <div className="overflow-hidden mb-16">
        <div
          className="code-divider-scroll whitespace-nowrap text-[10px] font-mono text-[#1a1a1a] select-none"
          style={{ animationDirection: 'reverse' }}
        >
          {CODE_LINE_4} {CODE_LINE_4} {CODE_LINE_4} {CODE_LINE_4}
        </div>
      </div>

      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.7 }}
        >
          <ScrambleText
            text="BEHIND THE PROJECT"
            className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight uppercase text-white font-outfit block"
            as="h2"
            duration={800}
            distortOnHover
          />
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-px border border-[#222222] mt-14">
          {team.map((member, i) => (
            <motion.div
              key={member.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.5, delay: i * 0.15 }}
              data-testid={`team-member-${i}`}
              className="group border border-[#222222] bg-[#050505] overflow-hidden"
            >
              {/* Photo */}
              <div className="relative overflow-hidden aspect-[3/4]">
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-full h-full object-cover grayscale opacity-70 group-hover:opacity-90 group-hover:grayscale-0 transition-all duration-700 scale-105 group-hover:scale-100"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
              </div>

              {/* Info */}
              <div className="p-5 sm:p-6">
                <h3 className="text-sm sm:text-base font-outfit text-white font-medium tracking-tight">
                  {member.name}
                </h3>
                <p className="text-[10px] sm:text-xs font-mono text-[#f6f6f6] tracking-[0.1em] mt-1">
                  {member.role}
                </p>
                <p className="text-[10px] font-mono text-[#f6f6f6] tracking-[0.1em] mt-1">
                  {member.detail}
                </p>
                <InstagramLink href={member.instagram} handle={member.handle} />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
